// ---------------------------------------------------------------------------
// Shared per-cell bit packing for the highlight feature.
//
// A CellState is a number whose bits are laid out as:
//   bits 0–1  game CellValue (each game maps its own values into these 2 bits)
//   bits 2–3  highlight colour index (0 = no highlight, 1–3 = colours A/B/C)
//   bits 4–7  edge-connectivity bits (top/right/bottom/left) used to group
//             adjacent highlighted cells into a single traced region
//
// The value bits are owned by each game; everything in this file operates only
// on the highlight + edge bits and is therefore game-agnostic.
// ---------------------------------------------------------------------------

export type CellState = number;

/** Extract the raw value bits (0–1). Each game maps these to its own CellValue. */
export const cellValueBits = (cell: CellState): number => cell & 0x03;

/** Replace only the value bits (0–1), preserving highlight + edge bits. */
export const withValueBits = (cell: CellState, bits: number): CellState => (cell & ~0x03) | (bits & 0x03);

export const isHighlighted = (cell: CellState): boolean => (cell & 0x0c) !== 0;

/** The highlight colour index stored in bits 2–3 (0 = no highlight, 1–3 = colours A/B/C). */
export const highlightColor = (cell: CellState): number => (cell >> 2) & 0x03;

/** Set the colour index (bits 2–3) without touching any other bits. */
export const setHighlightColor = (cell: CellState, colorIndex: number): CellState =>
  (cell & ~0x0c) | ((colorIndex & 0x03) << 2);

/** Mask covering all highlight-related bits (colour + edges = bits 2–7). */
export const HIGHLIGHT_BITS = 0xfc;

// ---------------------------------------------------------------------------
// Highlight edge connectivity
// ---------------------------------------------------------------------------

export type EdgeDir = 'top' | 'right' | 'bottom' | 'left';

const EDGE_BIT: Record<EdgeDir, number> = {
  top: 0x10,
  right: 0x20,
  bottom: 0x40,
  left: 0x80,
};

/** The opposite direction of a given edge (used to update the neighbour's bit). */
export const OPPOSITE_EDGE: Record<EdgeDir, EdgeDir> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

/** Row/col delta for each edge direction. */
export const EDGE_DELTA: Record<EdgeDir, [number, number]> = {
  top: [-1, 0],
  right: [0, 1],
  bottom: [1, 0],
  left: [0, -1],
};

/** Map a (dr, dc) step to an EdgeDir, or null if not a unit orthogonal step. */
export const deltaToEdgeDir = (dr: number, dc: number): EdgeDir | null => {
  if (dr === -1 && dc === 0) return 'top';
  if (dr === 0 && dc === 1) return 'right';
  if (dr === 1 && dc === 0) return 'bottom';
  if (dr === 0 && dc === -1) return 'left';
  return null;
};

export const hasEdge = (cell: CellState, dir: EdgeDir): boolean => (cell & EDGE_BIT[dir]) !== 0;

export const setEdge = (cell: CellState, dir: EdgeDir, on: boolean): CellState =>
  on ? cell | EDGE_BIT[dir] : cell & ~EDGE_BIT[dir];

const EDGE_DIRS: EdgeDir[] = ['top', 'right', 'bottom', 'left'];

/**
 * Fully connect every group of highlighted cells: any two orthogonally adjacent
 * same-colour cells that are already linked into the same group (via any chain
 * of edges) get a direct edge between them. A rectangle traced as a loop, U, or
 * snake thus ends up connected along all of its interior edges — so deleting
 * any single cell afterwards leaves the rest of the group connected. Adjacent
 * cells belonging to different groups are left alone.
 *
 * Mutates `cells` in place; returns true if any edge was added.
 */
export function solidifyHighlightGroups(cells: CellState[][]): boolean {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;

  // Label each highlighted cell with its group id (flood fill over edge bits).
  const group = Array.from({ length: rows }, () => new Array<number>(cols).fill(-1));
  let nextGroup = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!isHighlighted(cells[r][c]) || group[r][c] !== -1) continue;
      const id = nextGroup++;
      group[r][c] = id;
      const stack: [number, number][] = [[r, c]];
      while (stack.length > 0) {
        const [cr, cc] = stack.pop()!;
        for (const dir of EDGE_DIRS) {
          if (!hasEdge(cells[cr][cc], dir)) continue;
          const [dr, dc] = EDGE_DELTA[dir];
          const nr = cr + dr,
            nc = cc + dc;
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols || group[nr][nc] !== -1) continue;
          group[nr][nc] = id;
          stack.push([nr, nc]);
        }
      }
    }
  }

  // Add the missing edge between every adjacent same-colour pair in the same group.
  let changed = false;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (group[r][c] === -1) continue;
      for (const dir of ['right', 'bottom'] as EdgeDir[]) {
        if (hasEdge(cells[r][c], dir)) continue;
        const [dr, dc] = EDGE_DELTA[dir];
        const nr = r + dr,
          nc = c + dc;
        if (nr >= rows || nc >= cols) continue;
        if (group[nr][nc] !== group[r][c]) continue;
        if (highlightColor(cells[nr][nc]) !== highlightColor(cells[r][c])) continue;
        cells[r][c] = setEdge(cells[r][c], dir, true);
        cells[nr][nc] = setEdge(cells[nr][nc], OPPOSITE_EDGE[dir], true);
        changed = true;
      }
    }
  }
  return changed;
}

/**
 * Clear the colour + all edge bits on this cell, and remove the reciprocal edge bit
 * from every cell it was connected to. Mutates the `cells` array in place.
 */
export function clearHighlightAndEdges(cells: CellState[][], row: number, col: number): void {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;
  cells[row][col] = cells[row][col] & ~HIGHLIGHT_BITS;
  for (const dir of EDGE_DIRS) {
    const [dr, dc] = EDGE_DELTA[dir];
    const nr = row + dr,
      nc = col + dc;
    if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
      cells[nr][nc] = setEdge(cells[nr][nc], OPPOSITE_EDGE[dir], false);
    }
  }
}
